"use client"

import * as React from "react"
import { Check, ChevronDown, ChevronRight, MapPin } from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { cn } from "@/lib/utils"

interface City {
  id: string
  name: string
}

interface State {
  id: string
  name: string
  abbr: string
  cities: City[]
}

interface Region {
  id: string
  name: string
  states: State[]
}

const REGIONS: Region[] = [
  {
    id: "new-england",
    name: "New England",
    states: [
      { id: "ma", name: "Massachusetts", abbr: "MA", cities: [{ id: "boston", name: "Boston" }, { id: "worcester", name: "Worcester" }, { id: "cambridge", name: "Cambridge" }] },
      { id: "ct", name: "Connecticut", abbr: "CT", cities: [{ id: "hartford", name: "Hartford" }, { id: "new-haven", name: "New Haven" }] },
      { id: "ri", name: "Rhode Island", abbr: "RI", cities: [{ id: "providence", name: "Providence" }] },
      { id: "nh", name: "New Hampshire", abbr: "NH", cities: [{ id: "manchester-nh", name: "Manchester" }] },
      { id: "vt", name: "Vermont", abbr: "VT", cities: [{ id: "burlington", name: "Burlington" }] },
      { id: "me", name: "Maine", abbr: "ME", cities: [{ id: "portland-me", name: "Portland" }] },
    ]
  },
  {
    id: "mid-atlantic",
    name: "Mid-Atlantic",
    states: [
      { id: "ny", name: "New York", abbr: "NY", cities: [{ id: "nyc", name: "New York City" }, { id: "buffalo", name: "Buffalo" }, { id: "albany", name: "Albany" }] },
      { id: "nj", name: "New Jersey", abbr: "NJ", cities: [{ id: "newark", name: "Newark" }, { id: "jersey-city", name: "Jersey City" }] },
      { id: "pa", name: "Pennsylvania", abbr: "PA", cities: [{ id: "philly", name: "Philadelphia" }, { id: "pittsburgh", name: "Pittsburgh" }] },
      { id: "de", name: "Delaware", abbr: "DE", cities: [{ id: "wilmington", name: "Wilmington" }] },
      { id: "md", name: "Maryland", abbr: "MD", cities: [{ id: "baltimore", name: "Baltimore" }] },
      { id: "dc", name: "Washington D.C.", abbr: "DC", cities: [{ id: "dc-metro", name: "D.C. Metro" }] },
    ]
  },
  {
    id: "southeast",
    name: "Southeast",
    states: [
      { id: "fl", name: "Florida", abbr: "FL", cities: [{ id: "miami", name: "Miami" }, { id: "orlando", name: "Orlando" }, { id: "tampa", name: "Tampa" }] },
      { id: "ga", name: "Georgia", abbr: "GA", cities: [{ id: "atlanta", name: "Atlanta" }] },
      { id: "nc", name: "North Carolina", abbr: "NC", cities: [{ id: "charlotte", name: "Charlotte" }, { id: "raleigh", name: "Raleigh" }] },
      { id: "sc", name: "South Carolina", abbr: "SC", cities: [{ id: "charleston", name: "Charleston" }] },
      { id: "va", name: "Virginia", abbr: "VA", cities: [{ id: "richmond", name: "Richmond" }, { id: "nova", name: "Northern Virginia" }] },
      { id: "tn", name: "Tennessee", abbr: "TN", cities: [{ id: "nashville", name: "Nashville" }, { id: "memphis", name: "Memphis" }] },
    ]
  },
  {
    id: "midwest",
    name: "Midwest",
    states: [
      { id: "il", name: "Illinois", abbr: "IL", cities: [{ id: "chicago", name: "Chicago" }] },
      { id: "oh", name: "Ohio", abbr: "OH", cities: [{ id: "columbus", name: "Columbus" }, { id: "cleveland", name: "Cleveland" }, { id: "cincinnati", name: "Cincinnati" }] },
      { id: "mi", name: "Michigan", abbr: "MI", cities: [{ id: "detroit", name: "Detroit" }, { id: "grand-rapids", name: "Grand Rapids" }] },
      { id: "in", name: "Indiana", abbr: "IN", cities: [{ id: "indianapolis", name: "Indianapolis" }] },
      { id: "wi", name: "Wisconsin", abbr: "WI", cities: [{ id: "milwaukee", name: "Milwaukee" }, { id: "madison", name: "Madison" }] },
      { id: "mn", name: "Minnesota", abbr: "MN", cities: [{ id: "twin-cities", name: "Twin Cities" }] },
    ]
  },
  {
    id: "south-central",
    name: "South Central",
    states: [
      { id: "tx", name: "Texas", abbr: "TX", cities: [{ id: "houston", name: "Houston" }, { id: "dallas", name: "Dallas" }, { id: "austin", name: "Austin" }, { id: "san-antonio", name: "San Antonio" }] },
      { id: "ok", name: "Oklahoma", abbr: "OK", cities: [{ id: "okc", name: "Oklahoma City" }] },
      { id: "la", name: "Louisiana", abbr: "LA", cities: [{ id: "new-orleans", name: "New Orleans" }] },
      { id: "ar", name: "Arkansas", abbr: "AR", cities: [{ id: "little-rock", name: "Little Rock" }] },
    ]
  },
  {
    id: "mountain",
    name: "Mountain",
    states: [
      { id: "co", name: "Colorado", abbr: "CO", cities: [{ id: "denver", name: "Denver" }] },
      { id: "az", name: "Arizona", abbr: "AZ", cities: [{ id: "phoenix", name: "Phoenix" }, { id: "tucson", name: "Tucson" }] },
      { id: "ut", name: "Utah", abbr: "UT", cities: [{ id: "slc", name: "Salt Lake City" }] },
      { id: "nv", name: "Nevada", abbr: "NV", cities: [{ id: "vegas", name: "Las Vegas" }, { id: "reno", name: "Reno" }] },
      { id: "nm", name: "New Mexico", abbr: "NM", cities: [{ id: "albuquerque", name: "Albuquerque" }] },
    ]
  },
  {
    id: "pacific-northwest",
    name: "Pacific Northwest",
    states: [
      { id: "wa", name: "Washington", abbr: "WA", cities: [{ id: "seattle", name: "Seattle" }, { id: "tacoma", name: "Tacoma" }] },
      { id: "or", name: "Oregon", abbr: "OR", cities: [{ id: "portland", name: "Portland" }, { id: "eugene", name: "Eugene" }] },
      { id: "id", name: "Idaho", abbr: "ID", cities: [{ id: "boise", name: "Boise" }] },
    ]
  },
  {
    id: "california",
    name: "California",
    states: [
      { id: "ca-north", name: "Northern California", abbr: "CA-N", cities: [{ id: "sf", name: "San Francisco" }, { id: "oakland", name: "Oakland" }, { id: "sac", name: "Sacramento" }, { id: "sj", name: "San Jose" }] },
      { id: "ca-south", name: "Southern California", abbr: "CA-S", cities: [{ id: "la", name: "Los Angeles" }, { id: "sd", name: "San Diego" }, { id: "oc", name: "Orange County" }] },
    ]
  },
]

interface RegionSelectorProps {
  selectedRegions: string[]
  onSelectionChange: (regions: string[]) => void
}

export function RegionSelector({ selectedRegions, onSelectionChange }: RegionSelectorProps) {
  const [open, setOpen] = React.useState(false)
  const [expandedRegions, setExpandedRegions] = React.useState<string[]>([])
  const [expandedStates, setExpandedStates] = React.useState<string[]>([])

  const allSelected = selectedRegions.length === 0 || selectedRegions.includes("all")

  const toggleExpand = (id: string, type: "region" | "state") => {
    if (type === "region") {
      setExpandedRegions(prev => 
        prev.includes(id) ? prev.filter(r => r !== id) : [...prev, id]
      )
    } else {
      setExpandedStates(prev => 
        prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]
      )
    }
  }

  const toggleSelection = (id: string) => {
    if (id === "all") {
      onSelectionChange([])
      return
    }

    const newSelection = selectedRegions.filter(r => r !== "all")
    if (newSelection.includes(id)) {
      onSelectionChange(newSelection.filter(r => r !== id))
    } else {
      onSelectionChange([...newSelection, id])
    }
  }

  const isSelected = (id: string) => selectedRegions.includes(id)

  const getDisplayText = () => {
    if (allSelected || selectedRegions.length === 0) return "All Regions"
    if (selectedRegions.length === 1) {
      // Find the name for the single selection
      for (const region of REGIONS) {
        if (region.id === selectedRegions[0]) return region.name
        for (const state of region.states) {
          if (state.id === selectedRegions[0]) return state.name
          for (const city of state.cities) {
            if (city.id === selectedRegions[0]) return city.name
          }
        }
      }
      return "1 area"
    }
    return `${selectedRegions.length} areas`
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between bg-background/80 backdrop-blur-sm border-border hover:border-primary/50 hover:bg-background h-12 text-left"
        >
          <span className="flex items-center gap-2 truncate">
            <MapPin className="h-4 w-4 text-primary shrink-0" />
            <span className="truncate">{getDisplayText()}</span>
          </span>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0 bg-card border-border" align="start">
        <div className="max-h-96 overflow-y-auto">
          {/* All Regions Option */}
          <div
            className={cn(
              "flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-secondary/50 border-b border-border",
              allSelected && "bg-primary/10"
            )}
            onClick={() => toggleSelection("all")}
          >
            <div className={cn(
              "h-4 w-4 rounded border flex items-center justify-center shrink-0",
              allSelected ? "bg-primary border-primary" : "border-muted-foreground"
            )}>
              {allSelected && <Check className="h-3 w-3 text-primary-foreground" />}
            </div>
            <span className="font-medium">All Regions</span>
          </div>

          {/* Regions */}
          {REGIONS.map((region) => {
            const regionExpanded = expandedRegions.includes(region.id)
            const regionSelected = isSelected(region.id)

            return (
              <div key={region.id}>
                <div className={cn(
                  "flex items-center gap-2 px-3 py-2.5 cursor-pointer hover:bg-secondary/50",
                  regionSelected && "bg-primary/10"
                )}>
                  <button
                    onClick={(e) => { e.stopPropagation(); toggleExpand(region.id, "region") }}
                    className="p-0.5 hover:bg-secondary rounded"
                  >
                    <ChevronRight className={cn("h-4 w-4 transition-transform", regionExpanded && "rotate-90")} />
                  </button>
                  <div
                    className={cn(
                      "h-4 w-4 rounded border flex items-center justify-center shrink-0",
                      regionSelected ? "bg-primary border-primary" : "border-muted-foreground"
                    )}
                    onClick={() => toggleSelection(region.id)}
                  >
                    {regionSelected && <Check className="h-3 w-3 text-primary-foreground" />}
                  </div>
                  <span className="font-medium" onClick={() => toggleSelection(region.id)}>{region.name}</span>
                </div>

                {/* States */}
                {regionExpanded && region.states.map((state) => {
                  const stateExpanded = expandedStates.includes(state.id)
                  const stateSelected = isSelected(state.id)

                  return (
                    <div key={state.id}>
                      <div className={cn(
                        "flex items-center gap-2 pl-8 pr-3 py-2 cursor-pointer hover:bg-secondary/50",
                        stateSelected && "bg-primary/10"
                      )}>
                        <button
                          onClick={(e) => { e.stopPropagation(); toggleExpand(state.id, "state") }}
                          className="p-0.5 hover:bg-secondary rounded"
                        >
                          <ChevronRight className={cn("h-3 w-3 transition-transform", stateExpanded && "rotate-90")} />
                        </button>
                        <div
                          className={cn(
                            "h-4 w-4 rounded border flex items-center justify-center shrink-0",
                            stateSelected ? "bg-primary border-primary" : "border-muted-foreground"
                          )}
                          onClick={() => toggleSelection(state.id)}
                        >
                          {stateSelected && <Check className="h-3 w-3 text-primary-foreground" />}
                        </div>
                        <span className="text-sm" onClick={() => toggleSelection(state.id)}>
                          {state.name} <span className="text-muted-foreground">({state.abbr})</span>
                        </span>
                      </div>

                      {/* Cities */}
                      {stateExpanded && state.cities.map((city) => {
                        const citySelected = isSelected(city.id)

                        return (
                          <div
                            key={city.id}
                            className={cn(
                              "flex items-center gap-3 pl-16 pr-3 py-2 cursor-pointer hover:bg-secondary/50",
                              citySelected && "bg-primary/10"
                            )}
                            onClick={() => toggleSelection(city.id)}
                          >
                            <div className={cn(
                              "h-4 w-4 rounded border flex items-center justify-center shrink-0",
                              citySelected ? "bg-primary border-primary" : "border-muted-foreground"
                            )}>
                              {citySelected && <Check className="h-3 w-3 text-primary-foreground" />}
                            </div>
                            <span className="text-sm">{city.name}</span>
                          </div>
                        )
                      })}
                    </div>
                  )
                })}
              </div>
            )
          })}
        </div>
      </PopoverContent>
    </Popover>
  )
}
